/**
 * Base Effect Class for Poker Reborn Effects Engine
 *
 * Abstract base class that provides common functionality for all effects.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.2.1-2.2.4
 */

import { log } from '@/utils/log';
import type { Effect, EffectCallbacks, EffectOptions } from './Effect';
import {
  EffectType,
  EffectPriority,
  EffectState,
  type EffectTarget,
  type EffectResult,
} from './EffectTypes';

// =============================================================================
// ID GENERATION
// =============================================================================

let effectIdCounter = 0;

/**
 * Generate a unique effect ID.
 */
function generateEffectId(name: string): string {
  effectIdCounter++;
  return `${name}-${effectIdCounter}-${Date.now().toString(36)}`;
}

/**
 * Reset the effect ID counter (for testing).
 */
export function resetEffectIdCounter(): void {
  effectIdCounter = 0;
}

// =============================================================================
// BASE EFFECT CLASS (P2.2.1)
// =============================================================================

/**
 * Abstract base class for all effects.
 *
 * Subclasses must implement:
 * - `onPlay()`: The actual effect logic
 * - `onCancel()`: Cleanup when cancelled
 *
 * Optional overrides:
 * - `onPause()`: Pause logic
 * - `onResume()`: Resume logic
 */
export abstract class BaseEffect implements Effect {
  // ---------------------------------------------------------------------------
  // Identity (readonly)
  // ---------------------------------------------------------------------------

  readonly id: string;
  readonly type: EffectType;
  readonly name: string;

  // ---------------------------------------------------------------------------
  // Configuration (readonly)
  // ---------------------------------------------------------------------------

  readonly priority: EffectPriority;
  readonly target: EffectTarget;
  readonly tags: ReadonlyArray<string>;
  readonly duration: number;
  readonly cancellable: boolean;
  readonly pausable: boolean;

  // ---------------------------------------------------------------------------
  // Protected State
  // ---------------------------------------------------------------------------

  protected _state: EffectState = EffectState.PENDING;
  protected _progress = 0;
  protected _startTime = 0;
  protected _pausedAt = 0;
  protected _pausedDuration = 0;
  protected _callbacks: EffectCallbacks;
  protected _data: Record<string, unknown>;

  // Promise resolution for play()
  protected _resolve: ((result: EffectResult) => void) | null = null;

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  constructor(
    name: string,
    type: EffectType,
    target: EffectTarget,
    options: EffectOptions = {}
  ) {
    this.id = generateEffectId(name);
    this.name = name;
    this.type = type;
    this.target = target;

    this.priority = options.priority ?? EffectPriority.NORMAL;
    this.duration = options.duration ?? 0;
    this.tags = Object.freeze([...(options.tags ?? [])]);
    this._callbacks = options.callbacks ?? {};
    this._data = options.data ?? {};

    // Instant effects are not cancellable or pausable
    this.cancellable = type !== EffectType.INSTANT;
    this.pausable = type === EffectType.ANIMATION || type === EffectType.AMBIENT;
  }

  // ---------------------------------------------------------------------------
  // State Accessors
  // ---------------------------------------------------------------------------

  get state(): EffectState {
    return this._state;
  }

  get progress(): number {
    return this._progress;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle Methods (P2.2.2-P2.2.4)
  // ---------------------------------------------------------------------------

  /**
   * Play the effect.
   * Returns a promise that resolves when complete (or cancelled).
   */
  play(): Promise<EffectResult> {
    return new Promise((resolve) => {
      // Can only play from PENDING state
      if (this._state !== EffectState.PENDING) {
        log.effects.warning`Effect ${this.name} cannot play from state ${this._state}`;
        resolve(this.createResult(false, 'Effect already played'));
        return;
      }

      this._resolve = resolve;
      this._state = EffectState.PLAYING;
      this._startTime = performance.now();
      this._progress = 0;

      log.effects.debug`Effect ${this.name} starting (priority: ${this.priority})`;

      // Notify callback
      this._callbacks.onStart?.();

      // Run the actual effect
      try {
        this.onPlay();
      } catch (error) {
        log.effects.error`Effect ${this.name} error: ${error}`;
        this.completeWithError(error as Error);
      }
    });
  }

  /**
   * Cancel the effect.
   */
  cancel(): void {
    if (!this.cancellable) {
      log.effects.debug`Effect ${this.name} is not cancellable`;
      return;
    }

    if (
      this._state === EffectState.COMPLETED ||
      this._state === EffectState.CANCELLED ||
      this._state === EffectState.ERROR
    ) {
      return;
    }

    log.effects.debug`Effect ${this.name} cancelled`;

    this._state = EffectState.CANCELLED;

    // Run subclass cleanup
    try {
      this.onCancel();
    } catch (error) {
      log.effects.error`Effect ${this.name} cancel error: ${error}`;
    }

    // Notify callback
    this._callbacks.onCancel?.();

    // Resolve the play promise
    this._resolve?.(this.createResult(false, 'Cancelled'));
    this._resolve = null;
  }

  /**
   * Pause the effect.
   */
  pause(): void {
    if (!this.pausable) {
      log.effects.debug`Effect ${this.name} is not pausable`;
      return;
    }

    if (this._state !== EffectState.PLAYING) {
      return;
    }

    log.effects.debug`Effect ${this.name} paused`;

    this._state = EffectState.PAUSED;
    this._pausedAt = performance.now();

    this.onPause();
  }

  /**
   * Resume a paused effect.
   */
  resume(): void {
    if (this._state !== EffectState.PAUSED) {
      return;
    }

    log.effects.debug`Effect ${this.name} resumed`;

    this._state = EffectState.PLAYING;
    this._pausedDuration += performance.now() - this._pausedAt;
    this._pausedAt = 0;

    this.onResume();
  }

  // ---------------------------------------------------------------------------
  // Tag Support (P2.2.9)
  // ---------------------------------------------------------------------------

  /**
   * Check if this effect has a specific tag.
   */
  hasTag(tag: string): boolean {
    return this.tags.includes(tag);
  }

  /**
   * Check if this effect conflicts with another (same target).
   */
  conflictsWith(other: Effect): boolean {
    return (
      this.target.type === other.target.type && this.target.id === other.target.id
    );
  }

  // ---------------------------------------------------------------------------
  // Protected Helpers
  // ---------------------------------------------------------------------------

  /**
   * Mark the effect as complete.
   * Call this from onPlay() when the effect finishes.
   */
  protected complete(): void {
    if (this._state !== EffectState.PLAYING) {
      return;
    }

    this._state = EffectState.COMPLETED;
    this._progress = 1;

    log.effects.debug`Effect ${this.name} completed`;

    // Notify callback
    this._callbacks.onComplete?.();

    // Resolve the play promise
    this._resolve?.(this.createResult(true));
    this._resolve = null;
  }

  /**
   * Mark the effect as errored.
   */
  protected completeWithError(error: Error): void {
    if (
      this._state === EffectState.COMPLETED ||
      this._state === EffectState.CANCELLED ||
      this._state === EffectState.ERROR
    ) {
      return;
    }

    this._state = EffectState.ERROR;

    // Notify callback
    this._callbacks.onError?.(error);

    // Resolve the play promise
    this._resolve?.(this.createResult(false, error.message));
    this._resolve = null;
  }

  /**
   * Update progress (0-1).
   */
  protected updateProgress(progress: number): void {
    this._progress = Math.max(0, Math.min(1, progress));
    this._callbacks.onUpdate?.(this._progress);
  }

  /**
   * Get elapsed time in ms (excluding paused time).
   */
  protected getElapsedTime(): number {
    if (this._startTime === 0) return 0;

    let elapsed = performance.now() - this._startTime - this._pausedDuration;

    if (this._state === EffectState.PAUSED) {
      elapsed -= performance.now() - this._pausedAt;
    }

    return elapsed;
  }

  /**
   * Create an EffectResult.
   */
  protected createResult(success: boolean, error?: string): EffectResult {
    return {
      success,
      state: this._state,
      effectId: this.id,
      duration: this.getElapsedTime(),
      error,
    };
  }

  // ---------------------------------------------------------------------------
  // Abstract Methods (must be implemented by subclasses)
  // ---------------------------------------------------------------------------

  /**
   * Execute the effect. Called when play() is invoked.
   * Must call complete() when done.
   */
  protected abstract onPlay(): void;

  /**
   * Clean up when cancelled.
   * Should snap to end state if applicable.
   */
  protected abstract onCancel(): void;

  // ---------------------------------------------------------------------------
  // Optional Overrides
  // ---------------------------------------------------------------------------

  /**
   * Handle pause (optional override).
   */
  protected onPause(): void {
    // Default: no-op
  }

  /**
   * Handle resume (optional override).
   */
  protected onResume(): void {
    // Default: no-op
  }
}
